import React, { useState } from 'react';
import { Check, Copy, Phone, Moon, Sun, Coffee, MessageCircle, Footprints, ListChecks, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
// 1. Quote Block
export const QuoteBlock = ({
  children
}: {
  children: React.ReactNode;
}) => <motion.div initial={{
  opacity: 0,
  x: -10
}} whileInView={{
  opacity: 1,
  x: 0
}} viewport={{
  once: true
}} className="my-8 pl-6 border-l-4 border-amber-500 bg-navy-800/50 py-4 pr-4 rounded-r-lg shadow-[0_4px_20px_-4px_rgba(212,165,116,0.1)]" data-id="element-309">
    <p className="font-serif text-xl md:text-2xl italic text-cream-100 leading-relaxed" data-id="element-310">
      {children}
    </p>
  </motion.div>;
// 2. Bold Statement
export const BoldStatement = ({
  children
}: {
  children: React.ReactNode;
}) => <div className="my-10 text-center px-4" data-id="element-311">
    <h3 className="font-serif text-2xl md:text-3xl font-bold text-amber-500 leading-tight" data-id="element-312">
      {children}
    </h3>
  </div>;
// 3. Checklist Item
export const ChecklistItem = ({
  label,
  id
}: {
  label: string;
  id: string;
}) => {
  const [checked, setChecked] = useState(false);
  return <div onClick={() => setChecked(!checked)} className={`flex items-start space-x-4 p-4 rounded-lg cursor-pointer transition-all duration-300 border ${checked ? 'bg-navy-800 border-amber-500/50' : 'bg-transparent border-navy-800 hover:bg-navy-800/30'}`} data-id="element-313">
      <div className={`flex-shrink-0 w-6 h-6 rounded border-2 mt-0.5 flex items-center justify-center transition-colors ${checked ? 'bg-amber-500 border-amber-500' : 'border-cream-300'}`} data-id="element-314">
        {checked && <Check className="w-4 h-4 text-navy-950" strokeWidth={3} data-id="element-315" />}
      </div>
      <span className={`text-lg ${checked ? 'text-cream-300 line-through' : 'text-cream-100'}`} data-id="element-316">
        {label}
      </span>
    </div>;
};
// 4. Message Template
export const MessageTemplate = ({
  text,
  title
}: {
  text: string;
  title?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="my-6 bg-navy-800 rounded-lg border border-navy-900 overflow-hidden" data-id="element-317">
      {title && <div className="bg-navy-900 px-4 py-2 text-sm text-cream-300 font-medium uppercase tracking-wider" data-id="element-318">
          {title}
        </div>}
      <div className="p-6 relative" data-id="element-319">
        <p className="font-serif italic text-xl text-cream-100 pr-10" data-id="element-320">
          "{text}"
        </p>
        <button onClick={handleCopy} className="absolute top-4 right-4 p-2 text-amber-500 hover:text-amber-400 transition-colors" aria-label="Copiar mensaje" data-id="element-321">
          {copied ? <Check className="w-5 h-5" data-id="element-322" /> : <Copy className="w-5 h-5" data-id="element-323" />}
        </button>
      </div>
      {copied && <div className="bg-amber-500/10 text-amber-500 text-xs text-center py-1" data-id="element-324">
          ¡Copiado al portapapeles!
        </div>}
    </div>;
};
// 5. Night Card
interface NightCardProps {
  number: number;
  title: string;
  r1: string;
  r2: string;
  r3: string;
}
export const NightCard = ({
  number,
  title,
  r1,
  r2,
  r3
}: NightCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="mb-6 border border-navy-800 rounded-xl overflow-hidden bg-navy-900/40" data-id="element-325">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-5 text-left hover:bg-navy-800/50 transition-colors" data-id="element-326">
        <div className="flex items-center gap-4" data-id="element-327">
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-navy-800 text-amber-500 font-serif font-bold text-xl border border-amber-500/30" data-id="element-328">
            {number}
          </span>
          <div data-id="element-329">
            <h4 className="text-lg font-serif text-cream-100" data-id="element-330">{title}</h4>
            <p className="text-sm text-cream-300 mt-0.5" data-id="element-331">Ver ritual completo</p>
          </div>
        </div>
        <div className={`transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-332">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-500" data-id="element-333">
            <path d="m6 9 6 6 6-6" data-id="element-334" />
          </svg>
        </div>
      </button>

      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-335">
        <div className="p-5 pt-0 border-t border-navy-800 space-y-3" data-id="element-336">
          <div className="flex items-start gap-3 mt-4" data-id="element-337">
            <Moon className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" data-id="element-338" />
            <p className="text-cream-200" data-id="element-339">
              <span className="font-bold text-amber-500" data-id="element-340">R1:</span> {r1}
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-341">
            <ListChecks className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" data-id="element-342" />
            <p className="text-cream-200" data-id="element-343">
              <span className="font-bold text-amber-500" data-id="element-344">R2:</span> {r2}
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-345">
            <MessageCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" data-id="element-346" />
            <p className="text-cream-200" data-id="element-347">
              <span className="font-bold text-amber-500" data-id="element-348">R3:</span> {r3}
            </p>
          </div>
        </div>
      </div>
    </div>;
};
// 6. Emergency Card
export const EmergencyCard = () => <div className="my-12 p-6 md:p-8 rounded-xl bg-gradient-to-br from-navy-800 to-navy-900 border border-amber-500/30 shadow-lg relative overflow-hidden" data-id="element-349">
    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl -mr-16 -mt-16" data-id="element-350"></div>

    <div className="flex items-center gap-3 mb-6" data-id="element-351">
      <div className="p-2 bg-emergency-600/20 rounded-full" data-id="element-352">
        <Phone className="w-6 h-6 text-emergency-600" data-id="element-353" />
      </div>
      <h3 className="text-2xl font-serif font-bold text-cream-100" data-id="element-354">
        Si son las 2:00am...
      </h3>
    </div>

    <ol className="space-y-4 relative z-10" data-id="element-355">
      <li className="flex gap-3" data-id="element-356">
        <span className="font-bold text-amber-500" data-id="element-357">1.</span>
        <span className="text-cream-200" data-id="element-358">Siéntate. Luz cálida baja. Agua.</span>
      </li>
      <li className="flex gap-3" data-id="element-359">
        <span className="font-bold text-amber-500" data-id="element-360">2.</span>
        <span className="text-cream-200" data-id="element-361">
          Manos ocupadas: 5 platos o doblar 10 prendas.
        </span>
      </li>
      <li className="flex gap-3" data-id="element-362">
        <span className="font-bold text-amber-500" data-id="element-363">3.</span>
        <span className="text-cream-200" data-id="element-364">
          Escribe 4 líneas: "Estoy a salvo. Esto pasará. Mañana decido."
        </span>
      </li>
      <li className="flex gap-3" data-id="element-365">
        <span className="font-bold text-amber-500" data-id="element-366">4.</span>
        <span className="text-cream-200" data-id="element-367">
          Si necesitas: manda un emoji a tu contacto seguro.
        </span>
      </li>
      <li className="flex gap-3 items-center" data-id="element-368">
        <span className="font-bold text-amber-500" data-id="element-369">5.</span>
        <span className="text-cream-200" data-id="element-370">Si estás en riesgo: </span>
        <a href="tel:988" className="inline-flex items-center gap-2 px-3 py-1 bg-emergency-600 text-white rounded-full font-bold hover:bg-red-700 transition-colors" data-id="element-371">
          <Phone className="w-3 h-3" data-id="element-372" /> 988
        </a>
      </li>
    </ol>
  </div>;
// 7. Profile Card
export const ProfileCard = ({
  title,
  description,
  highlight
}: {
  title: string;
  description: string;
  highlight: string;
}) => <div className="p-6 rounded-lg bg-navy-800/50 border border-navy-800 mb-6" data-id="element-373">
    <h4 className="font-serif text-xl font-bold text-amber-500 mb-2" data-id="element-374">
      {title}
    </h4>
    <p className="text-cream-200 mb-4 leading-relaxed" data-id="element-375">{description}</p>
    <div className="text-sm font-medium text-cream-300 bg-navy-900/50 p-3 rounded border-l-2 border-amber-500" data-id="element-376">
      {highlight}
    </div>
  </div>;
// 8. Chapter Heading
export const ChapterHeading = ({
  number,
  title,
  id
}: {
  number: string;
  title: string;
  id: string;
}) => <div id={id} className="scroll-mt-24 mb-8 pt-8" data-id="element-377">
    <div className="flex items-center gap-3 mb-2" data-id="element-378">
      <span className="text-amber-500 font-bold tracking-widest text-sm uppercase" data-id="element-379">
        Capítulo {number}
      </span>
      <div className="h-px bg-navy-800 flex-grow" data-id="element-380"></div>
    </div>
    <h2 className="text-3xl md:text-4xl font-serif font-bold text-cream-100 leading-tight" data-id="element-381">
      {title}
    </h2>
  </div>;
// 9. Section Divider
export const SectionDivider = () => <div className="py-16 flex items-center justify-center opacity-50" data-id="element-382">
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-amber-500 to-transparent" data-id="element-383"></div>
    <div className="mx-4 text-amber-500" data-id="element-384">✦</div>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-amber-500 to-transparent" data-id="element-385"></div>
  </div>;